<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay, subDays, differenceInCalendarDays, parseISO } from 'date-fns';
// import { useI18n } from 'vue-i18n'; // Disponível para uso futuro com traduções
import { useTokensReport } from 'dashboard/composables/useTokensReport';

// Components
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportHeader from './components/ReportHeader.vue';
import DatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ConversationPopUp from 'dashboard/components/ConversationPopUp.vue';

// Tab Components
import DailyTab from './components/DailyTab.vue';
import MetricCard from './components/MetricCard.vue';

const router = useRouter();
// const { t } = useI18n(); // Desabilitado temporariamente - traduções hardcoded devido a limitações com computed

// Use the tokens report composable - names match backend (camelCase)
const {
  isLoading,
  fetchData,
  fetchComparisonData,
  billableInteractions,
  tokenLimit,
  tokenPeriodStart,
  tokenPeriodEnd,
  totalAiAudioInteractions,
  dailyChartData,
  hasDailyData,
  conversationMetrics,
  deletedBillableInteractions,
} = useTokensReport();

// Date range - starts empty; backend defaults to the account's current token cycle
// and we sync the picker from the response.
const dateStart = ref(null);
const dateEnd = ref(null);

const onDateRangeChanged = ([start, end]) => {
  dateStart.value = start;
  dateEnd.value = end;
  isComparing.value = false;
  comparisonData.value = null;
  loadData();
};

// Comparison
const isComparing = ref(false);
const isLoadingComparison = ref(false);
const comparisonData = ref(null);
const comparisonCooldown = ref(false);

const toggleComparison = async () => {
  if (isComparing.value) {
    isComparing.value = false;
    comparisonData.value = null;
    return;
  }

  if (comparisonCooldown.value) return;

  const days = differenceInCalendarDays(dateEnd.value, dateStart.value);
  const prevEnd = subDays(dateStart.value, 1);
  const prevStart = subDays(prevEnd, days);

  isLoadingComparison.value = true;
  const result = await fetchComparisonData(
    format(prevStart, 'yyyy-MM-dd'),
    format(prevEnd, 'yyyy-MM-dd')
  );
  isLoadingComparison.value = false;

  if (result) {
    comparisonData.value = result;
    isComparing.value = true;
    comparisonCooldown.value = true;
    setTimeout(() => { comparisonCooldown.value = false; }, 5000);
  }
};

const calcComparison = (current, previous) => {
  if (!isComparing.value || comparisonData.value === null) return null;
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};

// States
const selectedConversation = ref({ id: 'all', display_id: 'Todas as Conversas' }); // Tradução: t('REPORT.TOKENS_REPORT.ALL_CONVERSATIONS')
const showConversationModal = ref(false);
const modalConversations = ref([]);
const modalTitle = ref('');
const modalSubtitle = ref('');

// Alias for backward compatibility
const isLoadingCritical = computed(() => isLoading.value);
const isLoadingSecondary = computed(() => isLoading.value);

// Token limit is now defined per account in Super Admin (account.limits.token_limit)
// Check if token limit is exceeded
const isTokenLimitExceeded = computed(() => (billableInteractions.value || 0) > tokenLimit.value);

// Calculate usage percentage (capped at 100%)
const usagePercentage = computed(() => {
  const usage = Number(billableInteractions.value) || 0;
  const limit = Number(tokenLimit.value) || 0;
  if (limit <= 0) return 0;
  const percentage = Math.round((usage / limit) * 100);
  return Math.min(percentage, 100); // Cap at 100%
});

// NOTA: As traduções para este componente estão disponíveis em:
// app/javascript/dashboard/i18n/locale/pt_BR/report.json -> TOKENS_REPORT
// Porém, devido a limitações com useI18n no script setup com computed properties,
// estamos usando strings hardcoded temporariamente.
// Para usar as traduções: t('REPORT.TOKENS_REPORT.TOTAL_TOKENS'), etc.
const tokenMetricCards = computed(() => [
  {
    icon: 'i-lucide-zap',
    color: 'blue',
    title: 'Total no período',
    value: billableInteractions.value || 0,
    comparison: calcComparison(billableInteractions.value || 0, comparisonData.value?.billableInteractions),
    comparisonValue: comparisonData.value?.billableInteractions ?? null,
    comparisonUnit: 'tokens'
  },
  {
    icon: 'i-lucide-audio-lines',
    color: 'blue',
    title: 'Áudios gerados por IA',
    value: totalAiAudioInteractions.value || 0,
    format: 'number',
    comparison: calcComparison(totalAiAudioInteractions.value || 0, comparisonData.value?.totalAiAudioInteractions),
    comparisonValue: comparisonData.value?.totalAiAudioInteractions ?? null,
    comparisonUnit: 'áudios'
  },
  {
    icon: 'i-lucide-messages-square',
    color: 'blue',
    title: 'Conversas',
    value: (conversationMetrics.value || []).length,
    format: 'number',
    subtitle: 'Clique para ver detalhes',
    clickable: true,
    showModal: true
  }
]);

// Initialize data on mount
onMounted(async () => {
  // Load the bulk data
  await loadData();
});

// Data fetching now handled by composable with automatic pagination
const loadData = async () => {
  const params = {};

  if (dateStart.value && dateEnd.value) {
    params.since = format(dateStart.value, 'yyyy-MM-dd');
    params.until = format(dateEnd.value, 'yyyy-MM-dd');
  }

  // Add conversation_id parameter if a specific conversation is selected
  if (selectedConversation.value.id && selectedConversation.value.id !== 'all') {
    params.conversation_id = selectedConversation.value.id;
    params.debug = 'true';
  }

  await fetchData(params);
};

// On first response, sync the date picker with the backend-resolved cycle period.
watch([tokenPeriodStart, tokenPeriodEnd], ([start, end]) => {
  if (!start || !end) return;
  if (dateStart.value && dateEnd.value) return;
  dateStart.value = startOfDay(parseISO(start));
  dateEnd.value = endOfDay(parseISO(end));
}, { immediate: false });

// Fallback: if the backend didn't return period info after loading completes,
// initialize the picker to the current calendar month so the UI stays usable.
watch(isLoading, value => {
  if (value) return;
  if (dateStart.value && dateEnd.value) return;
  dateStart.value = startOfDay(startOfMonth(new Date()));
  dateEnd.value = endOfDay(endOfMonth(new Date()));
});

const navegarParaMetricas = () => {
  router.push({ name: 'ouroboros_metrics' });
};

const navegarParaTabelas = () => {
  router.push({ name: 'ouroboros_tables' });
};

// Crosshair plugin - draws a vertical line on hover
const crosshairPlugin = {
  id: 'crosshair',
  afterDraw(chart) {
    const { ctx, tooltip, chartArea: { top, bottom } } = chart;
    if (!tooltip || !tooltip.opacity) return;

    const x = tooltip.caretX;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
    ctx.stroke(); 
    ctx.restore();
  },
};

// Chart options
const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  scales: {
    x: {
      display: true,
      title: {
        display: true,
        text: 'Data' // Tradução disponível: t('REPORT.TOKENS_REPORT.CHART_DATE_LABEL')
      }
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      title: {
        display: true,
        text: 'Quantidade' // Tradução disponível: t('REPORT.TOKENS_REPORT.CHART_QUANTITY_LABEL')
      },
      beginAtZero: true,
      ticks: {
        stepSize: 1,
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      bodyFont: { size: 14 },
      titleFont: { size: 14 },
      padding: 12,
    }
  }
};

// Close conversation modal
const closeConversationModal = () => {
  showConversationModal.value = false;
  modalConversations.value = [];
  modalTitle.value = '';
  modalSubtitle.value = '';
};

// Handle metric card click
const handleMetricCardClick = (card) => {
  // Only handle cards with showModal flag
  if (!card.showModal) return;

  // Backend already filters for AI conversations
  modalConversations.value = conversationMetrics.value || [];
  modalTitle.value = 'Conversas com Interações de IA';
  const count = (conversationMetrics.value || []).length;
  const deleted = deletedBillableInteractions.value || 0;
  modalSubtitle.value = deleted > 0
    ? `${count} conversas encontradas · ${deleted} interações são de conversas excluídas e não aparecem nesta lista`
    : `${count} conversas encontradas`;
  showConversationModal.value = true;
};
</script>

<template>
  <div class="reports--wrapper bg-n-background w-full">
    <ReportHeader header-title="Consumo de Tokens">
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 rounded-xl py-2 px-3 h-9 text-sm outline-1 shadow outline transition-all cursor-pointer"
          :class="isComparing
            ? 'outline-woot-500 bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300'
            : 'outline-n-container bg-n-solid-2 hover:bg-n-slate-1 dark:hover:bg-n-solid-3 hover:outline-woot-500 text-n-slate-11'"
          :disabled="isLoadingComparison || comparisonCooldown"
          @click="toggleComparison"
        >
          <span class="i-lucide-arrow-left-right text-sm" />
          <span>{{ isLoadingComparison ? 'Carregando...' : isComparing ? 'Comparando' : 'Comparar' }}</span>
        </button>
        <DatePicker
          v-if="dateStart && dateEnd"
          align="right"
          :initial-start-date="dateStart"
          :initial-end-date="dateEnd"
          @date-range-changed="onDateRangeChanged"
        />
        <div
          v-else
          class="h-9 w-40 rounded-xl bg-n-alpha-2 dark:bg-n-solid-1 animate-pulse"
        ></div>
      </div>
    </ReportHeader>
    <div class="w-full pb-12">

      <!-- Loading Estado - Skeleton for cards -->
      <div v-if="isLoadingCritical" class="space-y-8">
        <!-- Loading message with spinner -->
        <div class="flex items-center gap-2 mb-4">
          <Spinner size="20" class="text-woot-600 dark:text-woot-400" />
          <p class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Analisando dados. Isso pode levar alguns minutos.
          </p>
        </div>

        <!-- Skeleton: metric cards (3 on first row, 2 on second) -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div v-for="i in 3" :key="'sk-top-'+i" class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 animate-pulse">
            <div class="flex items-center justify-between">
              <div class="h-5 w-24 rounded bg-slate-200 dark:bg-slate-700"></div>
              <div class="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-700"></div>
            </div>
            <div class="mt-4 h-8 w-32 rounded bg-slate-200 dark:bg-slate-700"></div>
            <div class="mt-2 h-4 w-40 rounded bg-slate-200 dark:bg-slate-700"></div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="i in 2" :key="'sk-bot-'+i" class="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 animate-pulse">
            <div class="flex items-center justify-between">
              <div class="h-5 w-28 rounded bg-slate-200 dark:bg-slate-700"></div>
              <div class="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-700"></div>
            </div>
            <div class="mt-4 h-8 w-28 rounded bg-slate-200 dark:bg-slate-700"></div>
            <div class="mt-2 h-4 w-44 rounded bg-slate-200 dark:bg-slate-700"></div>
          </div>
        </div>

        <!-- Skeleton: daily chart card -->
        <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-1 p-6">
          <div class="flex items-center justify-between">
            <div class="h-5 w-40 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
            <div class="h-5 w-24 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
          </div>
          <div class="mt-6 h-56 w-full overflow-hidden rounded-lg bg-slate-200/70 dark:bg-slate-700/70 animate-pulse"></div>
        </div>

        <!-- Skeleton: info section -->
        <section class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
          <div class="h-5 w-64 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
          <div class="mt-4 space-y-3">
            <div class="h-4 w-full rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
            <div class="h-4 w-11/12 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
            <div class="h-4 w-10/12 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"></div>
          </div>
        </section>
      </div>
      
      <div v-else>

        <!-- Métricas de Tokens -->
        <div class="mb-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- First 2 cards in a row -->
            <MetricCard
              v-for="(card, index) in tokenMetricCards.slice(0, 2)"
              :key="`token-${index}`"
              :icon="card.icon"
              :color="card.color"
              :title="card.title"
              :value="card.value"
              :suffix="card.suffix"
              :subtitle="card.subtitle"
              :format="card.format || 'number'"
              :clickable="card.clickable || false"
              :link="card.link"
              :linkText="card.linkText"
              :comparison="card.comparison"
              :comparison-value="card.comparisonValue"
              :comparison-unit="card.comparisonUnit"
              @click="handleMetricCardClick(card)"
            />
          </div>
          <div class="grid grid-cols-1 gap-4 mt-4">
            <!-- Last card -->
            <MetricCard
              v-for="(card, index) in tokenMetricCards.slice(2)"
              :key="`token-${index + 2}`"
              :icon="card.icon"
              :color="card.color"
              :title="card.title"
              :value="card.value"
              :suffix="card.suffix"
              :subtitle="card.subtitle"
              :format="card.format || 'number'"
              :clickable="card.clickable || false"
              :link="card.link"
              :linkText="card.linkText"
              :comparison="card.comparison"
              :comparison-value="card.comparisonValue"
              :comparison-unit="card.comparisonUnit"
              @click="handleMetricCardClick(card)"
            />
          </div>
        </div>

        <!-- Análise Diária -->
        <div class="mb-8 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
          <DailyTab
            :is-loading-secondary="isLoadingSecondary"
            :has-daily-data="hasDailyData"
            :daily-chart-data="dailyChartData"
            :line-chart-options="lineChartOptions"
            :chart-plugins="[crosshairPlugin]"
          />
        </div>

        <!-- INFO: Como funcionam os tokens? -->
        <section class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
          <h4 class="text-base font-semibold flex items-center mb-3 text-woot-600 dark:text-woot-600">
            <span class="i-lucide-info mr-2"></span>
            Como funcionam os tokens?
          </h4>

          <div class="text-base space-y-4">
            <div class="text-slate-900 dark:text-slate-300">
              Os tokens são medidos por interação. Uma interação <strong>inicia com uma mensagem do cliente e finaliza quando a I.A responde</strong>. Cada interação completa da I.A custa 1 Token. Cliente pergunta e agente IA responde = 1 Token.
            </div>

            <!-- Chat Preview Example -->
            <div class="mt-4">
              <p class="text-xs text-slate-600 dark:text-slate-400 mb-2 font-medium">Exemplo de conversa:</p>

              <div class="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-none">
                <!-- Chat messages container -->
                <div class="space-y-3">
                    <!-- Client message 1 -->
                    <div class="flex items-end gap-2">
                      <div class="w-8 h-8 rounded-full bg-slate-400 flex items-center justify-center text-n-slate-2 text-xs font-medium flex-shrink-0">
                        C
                      </div>
                      <div class="max-w-[75%]">
                        <div class="bg-white dark:bg-slate-700 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm border border-slate-200 dark:border-slate-600">
                          <p class="text-sm text-slate-900 dark:text-slate-100">
                            Bom dia, tudo bem? Podem me falar sobre o produto x?
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Client message 2 -->
                    <div class="flex items-end gap-2">
                      <div class="w-8 h-8 rounded-full bg-slate-400 flex items-center justify-center text-n-slate-2 text-xs font-medium flex-shrink-0">
                        C
                      </div>
                      <div class="max-w-[75%]">
                        <div class="bg-white dark:bg-slate-700 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm border border-slate-200 dark:border-slate-600">
                          <p class="text-sm text-slate-900 dark:text-slate-100">
                            Estou interessada nele.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Agent message 1 -->
                    <div class="flex items-end gap-2 justify-end">
                      <div class="max-w-[75%]">
                        <div class="bg-woot-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                          <p class="text-sm">
                            Bom dia, tudo bem, e com você?
                          </p>
                        </div>
                      </div>
                      <div class="w-8 h-8 rounded-full bg-woot-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        IA
                      </div>
                    </div>
                    
                    <!-- Agent message 2 -->
                    <div class="flex items-end gap-2 justify-end">
                      <div class="max-w-[75%]">
                        <div class="bg-woot-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                          <p class="text-sm">
                            Posso te passar os detalhes do produto, sim!
                          </p>
                        </div>
                      </div>
                      <div class="w-8 h-8 rounded-full bg-woot-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        IA
                      </div>
                    </div>
                    
                    <!-- Agent message 3 -->
                    <div class="flex items-end gap-2 justify-end">
                      <div class="max-w-[75%]">
                        <div class="bg-woot-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                          <p class="text-sm">
                            O preço dele é bem acessível...
                          </p>
                        </div>
                      </div>
                      <div class="w-8 h-8 rounded-full bg-woot-600 flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                        IA
                      </div>
                    </div>

                    <!-- Token indicator -->
                    <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                      <div class="flex items-center justify-center">
                        <div class="inline-flex items-center px-4 py-2 bg-woot-50 dark:bg-woot-900/30 rounded-full border-2 border-woot-500 dark:border-woot-600">
                          <span class="i-lucide-zap text-woot-600 dark:text-woot-400 mr-2"></span>
                          <span class="text-sm font-semibold text-woot-700 dark:text-woot-300">
                            Essa conversa = 1 Interação = 1 Token
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- /Token indicator -->
                </div>
                <!-- /space-y-3 -->
              </div>
              <!-- /chat preview box -->
            </div>
            <!-- /mt-4 -->
          </div>
          <!-- /text-sm -->
        </section>
        <!-- /INFO: Como funcionam os tokens? -->

      </div>
    </div>
  </div>
    
  <!-- Conversation Modal -->
  <Transition name="modal-fade">
    <ConversationPopUp
      v-if="showConversationModal"
      :conversations="modalConversations"
      :title="modalTitle"
      :subtitle="modalSubtitle"
      @close="closeConversationModal"
    />
  </Transition>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
